import { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import PropTypes from "prop-types";

export const EmailBody = ({
  setEmailBody,
  emailBody,
  className,
  showLinkAndImageOptions = true,
  quillRef,
  placeholderText = "Compose and email...",
}) => {
  const quill = quillRef && quillRef.current && quillRef.current.getEditor();
  const fontNames = [
    "arial",
    "times new roman",
    "monospace",
    "arial black",
    "arial narrow",
    "comic sans ms",
    "garamond",
    "georgia",
    "tahoma",
    "trebuchet ms",
    "verdana",
    "brush script mt",
  ];
  const TRIM_NEWLINES = /("\\\\n(\\\\n){0,3}"\}\]\})$/;

  const [formatValues, setFormatValues] = useState({});
  const [formatLength, setFormatLength] = useState(0);
  const quillContents = quill && quill.getContents();

  useEffect(() => {
    if (quillRef.current && quillContents) {
      const quill = quillRef.current.getEditor();
      if (
        quillContents.ops[quillContents.ops.length - 2] &&
        quillContents.ops[quillContents.ops.length - 2].insert.length === 1
      ) {
        setFormatValues(quillContents.ops[quillContents.ops.length - 2].attributes);
        setFormatLength(quillContents.ops.length);
      }
      if (
        (quillContents.ops[0].insert === "\n" || quillContents.ops[0].insert === "\n\n") &&
        Object.keys(formatValues).length > 0
      ) {
        setFormatValues({});
        quill.removeFormat(0, 1);
      }
      if (quillContents.ops.length < formatLength - 1 && quillContents.ops.length !== 0) {
        for (const property in formatValues) {
          quill.format(`${property}`, `${formatValues[property]}`);
        }
      }
    }
  }, [quillContents, formatValues, formatLength, quillRef]);

  useEffect(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const handleClickOutside = (event) => {
        const pattern = /(?:^|\s)ql-picker-label(?:\s|$)/;
        if (
          (quill.hasFocus() || document.activeElement.className.match(pattern)) &&
          !quill.container.contains(event.target) &&
          quill.getFormat().background === "#ACCEF7"
        ) {
          quill.format("background", false);
        }
      };
      quill.on("selection-change", function (range) {
        if (range && range.length === 0) {
          quill.format("background", false);
        }
      });
      document.addEventListener("click", handleClickOutside);
      return () => {
        document.removeEventListener("click", handleClickOutside);
      };
    }
  }, [quillRef]);

  if (quillRef.current) {
    const quill = quillRef.current.getEditor();
    quill.keyboard.bindings[9].unshift({
      key: 9,
      handler: () => quill.blur(),
    });
    const updatedContent = JSON.stringify(quill.getContents());
    if (updatedContent.match(TRIM_NEWLINES) && quill.hasFocus()) {
      let child = document.querySelector(".ql-size").firstElementChild;
      if (child) {
        child.className = "ql-picker-label ql-active";
        child.setAttribute("data-value", quill.getFormat().size);
      }
    }
  }

  const Quill = ReactQuill.Quill;
  const FontAttributor = Quill.import("attributors/style/font");
  FontAttributor.whitelist = fontNames;
  Quill.register(FontAttributor, true);
  const Size = Quill.import("attributors/style/size");
  Size.whitelist = ["13px", "16px", "19px"];
  Quill.register(Size, true);
  Quill.register(Quill.import("attributors/style/direction"), true);
  Quill.register(Quill.import("attributors/style/align"), true);
  Quill.register(Quill.import("attributors/style/color"), true);

  let Link = Quill.import('formats/link')

  Link.sanitize = function (url) {
    if (!(url.startsWith('http://') || url.startsWith('https://'))) {
      return 'http://' + url;
    }
    return url;
  }

  const modules = {
    toolbar: [
      [
        { font: fontNames },
        { size: ["13px", "16px", "19px"] },
        "bold",
        "italic",
        "underline",
        { color: [] },
        { background: [] },
      ],
      [{ align: "" }, { align: "center" }, { align: "right" }, { list: "ordered" }, { list: "bullet" }],
      showLinkAndImageOptions ? [{ link: "link" }, { image: "image" }] : [],
    ],
  };

  return (
    <ReactQuill
      className={className || "email-form--body-inner"}
      value={emailBody}
      onChange={(e) => setEmailBody(e, quillRef)}
      placeholder={placeholderText}
      modules={modules}
      theme="snow"
      ref={quillRef}
    />
  );
};

EmailBody.propTypes = {
  emailBody: PropTypes.string,
  setEmailBody: PropTypes.func,
  className: PropTypes.string,
  showLinkAndImageOptions: PropTypes.bool,
  quillRef: PropTypes.object,
  placeholderText: PropTypes.string,
};

export default EmailBody;
